/* ============================================================
   TV CONTEXT — the store's public surface for components
   ------------------------------------------------------------
   Components consume this via useTv(). They may ONLY:
     • read `state` (and the derived `current` channel),
     • call `act()` / the few specific action methods, and
     • attach the store-owned callback refs to DOM nodes.
   They never hold their own state (lint-enforced in src/components).
   All engine instances + imperative TV logic live in TvProvider.
   ============================================================ */
import { createContext, useContext, type PointerEvent as ReactPointerEvent } from 'react'
import type { Channel, DisplaySettingKey, TvState } from './types'

/** A callback ref: components spread it onto a DOM node; the store keeps it. */
export type NodeRef<T extends Element = HTMLElement> = (node: T | null) => void

/** The remote/keyboard verbs — mirrors the prototype's doAction switch. */
export type RemoteAction =
  | 'power'
  | 'chUp'
  | 'chDown'
  | 'volUp'
  | 'volDown'
  | 'progPrev'
  | 'progNext'
  | 'play'
  | 'mute'
  | 'guide'
  | 'ok'
  | 'back'
  | 'digit'
  | 'fullscreen'
  | 'live'
  | 'remote' // summon/dismiss the on-screen remote (esp. in fullscreen, where there's no hover)

export interface TvApi {
  state: TvState
  channels: Channel[]
  current: Channel

  /** Gated dispatch used by the remote + keyboard. No-ops while audio is locked. */
  act(action: RemoteAction, payload?: number): void
  /** Tune to a channel from the EPG (guide is only reachable while unlocked). */
  guideSelectAndTune(index: number): void
  /** Play a guide program block: future → from start, now-airing → go live. */
  guidePlayBlock(channelIndex: number, videoOrdinal: number, live: boolean): void
  /** Go live on a channel from the guide (its channel cell). */
  guidePlayChannelLive(channelIndex: number): void
  /** Zoom the guide's visible time span (dir +1 = wider, -1 = narrower). */
  guideZoom(dir: 1 | -1): void
  /** Set the guide search query (also activates search mode). */
  guideSearchSet(text: string): void
  /** Collapse / expand the remote's LCD touchscreen panel (touch). */
  remoteCollapse(): void
  /** The screen "whack": user click on the tube; toggles play once unlocked. */
  whackScreen(): void
  /** Begin dragging the remote by its head. */
  startRemoteDrag(e: ReactPointerEvent): void
  /** Fullscreen: click the peeking remote to bring it fully in-window. */
  remoteActivate(): void
  /** Fullscreen: reset the in-window remote's 5s auto-hide (hover/interaction). */
  remoteKeepAlive(): void
  /** Switch the remote's LCD touchscreen view (dial / list / options). */
  setTouchScreen(view: TvState['touchScreen']): void
  /** Resize the on-screen remote (dir +1 bigger, -1 smaller), clamped + persisted. */
  setRemoteScale(dir: 1 | -1): void
  /** Toggle a display setting from the options screen (persisted). */
  toggleSetting(key: DisplaySettingKey, origin?: { left: number; top: number }): void
  /** Move the runaway ad-disable confirmation button away from the pointer. */
  adConfirmPointerMove(point: { clientX: number; clientY: number }): void
  /** Idle-wander the runaway button when the pointer isn't near it. */
  adConfirmIdleTick(point: { clientX: number; clientY: number } | null): void
  /** Confirm disabling ad bumpers after catching the runaway button. */
  confirmAdBumpersOff(): void
  /** Jump to a queue index from the LCD video list. */
  playProgram(index: number): void
  /** Toggle shuffle for the current channel (keeps the current video playing). */
  shuffleToggle(): void
  /** Toggle YouTube subtitles/captions for the active and standby players. */
  toggleSubtitles(): void
  /** Dismiss the "cleaner extension available" hint (persists the choice). */
  dismissExtensionHint(): void
  /** Report a bug: capture the session to Sentry and copy the session id to clipboard. */
  reportBug(): void

  // --- store-owned DOM nodes (no useRef in components) ---
  registerTv: NodeRef<HTMLDivElement>
  registerScreen: NodeRef<HTMLDivElement>
  registerPlayerHostA: NodeRef<HTMLDivElement>
  registerPlayerHostB: NodeRef<HTMLDivElement>
  registerSnow: NodeRef<HTMLCanvasElement>
  registerNoSignalBox: NodeRef<HTMLDivElement>
  registerRemote: NodeRef<HTMLDivElement>
  registerBumpCanvas: NodeRef<HTMLCanvasElement>
  registerBumpOverlay: NodeRef<HTMLElement>
}

export const TvContext = createContext<TvApi | null>(null)

export function useTv(): TvApi {
  const api = useContext(TvContext)
  if (!api) throw new Error('useTv must be used within <TvProvider>')
  return api
}
